import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Button, Callout, Checkbox, Dialog, Field, TextArea, TextInput } from '../../../ui';
import { STAGE_LABELS, Stage, errorText, moveCandidate, needsReason } from '../../../TeacherHiring/hiringApi';

/** What the service does on arriving in each stage, so nobody is surprised by a WhatsApp going out. */
const AUTOMATION: Partial<Record<Stage, string>> = {
  SCREENING: 'The AI voice agent will call them in the next calling window.',
  INTERVIEW_1: 'They will get the brochure and their interview link on WhatsApp and email.',
  INTERVIEW_2: 'Next, schedule the academic interview from their profile.',
  TRAINING: 'If you give a batch, they get a welcome message with it.',
  CONTRACT: 'If you give a contract link, they get it on WhatsApp and email.',
  ONBOARDED: 'They get a welcome-to-the-faculty message.',
  NEW: 'Reopens the application; screening starts again from scratch.',
};

/** Stages whose message the agent can switch off. Rejection is opt-in. */
const NOTIFIES: Stage[] = ['INTERVIEW_1', 'TRAINING', 'CONTRACT', 'ONBOARDED', 'REJECTED'];

interface Props {
  candidate: { id: string; name: string; stage: Stage } | null;
  toStage: Stage | null;
  onClose: () => void;
  onMoved: () => void;
}

const MoveDialog: React.FC<Props> = ({ candidate, toStage, onClose, onMoved }) => {
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(true);
  const [trainingBatch, setTrainingBatch] = useState('');
  const [trainingStartAt, setTrainingStartAt] = useState('');
  const [contractUrl, setContractUrl] = useState('');
  const [cmsTeacherId, setCmsTeacherId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setReason('');
    setNotify(toStage !== 'REJECTED');
    setTrainingBatch('');
    setTrainingStartAt('');
    setContractUrl('');
    setCmsTeacherId('');
    setError(null);
  }, [candidate?.id, toStage]);

  if (!candidate || !toStage) return null;
  const reasonRequired = needsReason(toStage);

  const submit = async () => {
    if (reasonRequired && !reason.trim()) {
      setError('Please give a reason.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await moveCandidate(candidate.id, {
        toStage,
        reason: reason.trim() || undefined,
        notify: NOTIFIES.includes(toStage) ? notify : undefined,
        trainingBatch: trainingBatch.trim() || undefined,
        trainingStartAt: trainingStartAt ? new Date(trainingStartAt).toISOString() : undefined,
        contractUrl: contractUrl.trim() || undefined,
        cmsTeacherId: cmsTeacherId.trim() || undefined,
      });
      toast.success(`${candidate.name} moved to ${STAGE_LABELS[toStage]}`);
      onMoved();
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      persistent
      title={`Move ${candidate.name} to ${STAGE_LABELS[toStage]}?`}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant={toStage === 'REJECTED' ? 'danger' : 'primary'} onClick={submit} loading={saving}>
            Move to {STAGE_LABELS[toStage]}
          </Button>
        </>
      }
    >
      <div className="th-form">
        <p className="ds-text-subtle">From {STAGE_LABELS[candidate.stage]}.</p>
        {AUTOMATION[toStage] && <Callout tone="info">{AUTOMATION[toStage]}</Callout>}

        {reasonRequired && (
          <Field label="Reason" required hint="Shown on their card and timeline.">
            {(ctl) => <TextArea {...ctl} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />}
          </Field>
        )}
        {toStage === 'TRAINING' && (
          <div className="th-form__row">
            <Field label="Training batch">
              {(ctl) => <TextInput {...ctl} value={trainingBatch} onChange={(e) => setTrainingBatch(e.target.value)} placeholder="e.g. TB-Nov-01" />}
            </Field>
            <Field label="Starts">
              {(ctl) => <TextInput {...ctl} type="datetime-local" value={trainingStartAt} onChange={(e) => setTrainingStartAt(e.target.value)} />}
            </Field>
          </div>
        )}
        {toStage === 'CONTRACT' && (
          <Field label="Contract link" hint="The e-sign link (Leegality, DocuSign, Zoho Sign).">
            {(ctl) => <TextInput {...ctl} type="url" value={contractUrl} onChange={(e) => setContractUrl(e.target.value)} placeholder="https://" />}
          </Field>
        )}
        {toStage === 'ONBOARDED' && (
          <Field label="CMS teacher id" hint="After creating them under Profiles → Teacher.">
            {(ctl) => <TextInput {...ctl} value={cmsTeacherId} onChange={(e) => setCmsTeacherId(e.target.value)} />}
          </Field>
        )}
        {NOTIFIES.includes(toStage) && (
          <Checkbox
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
            label={toStage === 'REJECTED' ? 'Send them a polite rejection message' : 'Message the candidate'}
            hint="WhatsApp and email"
          />
        )}
        {error && <Callout tone="danger">{error}</Callout>}
      </div>
    </Dialog>
  );
};

export default MoveDialog;
